import { z } from "zod";

/**
 * FACEIT, read through its Data API with the server's key: players found from their
 * Steam ID, the team's FACEIT team, matches (recent ones and a championship's), and a
 * demo's match to fill the import. Downloading demos waits for FACEIT's Downloads API.
 */

/** A FACEIT match id, as in the match room's address and the demo's file name: "1-" and a UUID. */
export const FACEIT_MATCH_ID = /\b1-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i;
export const FaceitMatchId = z.string().regex(new RegExp(`^${FACEIT_MATCH_ID.source.replace(/\\b/g, "")}$`, "i"), "Not a FACEIT match id.");

/** The FACEIT match a demo file comes from, read from its name ("1-…-1-1.dem.gz"). */
export function faceitMatchIdIn(fileName: string): string | null {
  const m = FACEIT_MATCH_ID.exec(fileName);
  return m ? m[0].toLowerCase() : null;
}

export interface FaceitPlayerRef {
  id: string;
  nickname: string;
  avatar: string | null;
  /** Their Steam ID (64-bit), when FACEIT gives it. */
  steamId: string | null;
  /** Skill level 1–10. */
  level: number | null;
  url: string;
}

export interface FaceitTeamRef {
  id: string;
  name: string;
  avatar: string | null;
  url: string;
}

export const FACEIT_MATCH_STATUSES = ["upcoming", "ongoing", "finished", "cancelled"] as const;
export type FaceitMatchStatus = (typeof FACEIT_MATCH_STATUSES)[number];

/** One side of a FACEIT match. */
export interface FaceitFaction {
  /** The FACEIT team for a championship match; a pick-up side's id otherwise. */
  id: string;
  name: string;
  avatar: string | null;
  players: FaceitPlayerRef[];
  /** Maps won (BO3, BO5) or rounds (BO1), as FACEIT counts the result. */
  score: number | null;
}

export interface FaceitMatch {
  id: string;
  url: string;
  competition: { id: string | null; name: string; type: "championship" | "matchmaking" | "hub" | "other" };
  /** "Round 3", "Group B · Round 2", "Playoffs"…: for championships, when FACEIT says. */
  stage: string | null;
  bestOf: number;
  status: FaceitMatchStatus;
  scheduledAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  factions: [FaceitFaction, FaceitFaction];
  /** 0 or 1, null while not over or for a draw. */
  winner: 0 | 1 | null;
  /** The maps picked, in order, when known (de_mirage…). */
  maps: string[];
  /** Each map's score, in the order played: [faction 0, faction 1]. */
  mapScores: [number, number][];
}

/** What 69LY knows of a FACEIT match for a team: which side is ours, and whether it is in the library. */
export interface FaceitListMatch extends FaceitMatch {
  /** Our side (0 or 1), from the team's FACEIT team or its members; null when it is none of ours (scouting). */
  ours: 0 | 1 | null;
  /** How many of the team played, on our side. */
  members: number;
  label: "team" | "stack" | "solo" | "scouting";
  /** The library's match holding it, once imported. */
  libraryMatchId: string | null;
}

export interface FaceitOpponent {
  team: FaceitTeamRef;
  /** Our matches against them (played or to come). */
  versus: FaceitListMatch[];
  /** Their other matches in the championship: what to scout. */
  matches: FaceitListMatch[];
}

export interface FaceitTournament {
  id: string;
  name: string;
  url: string;
  status: "upcoming" | "ongoing" | "finished" | "other";
  /** Ours, newest first. */
  matches: FaceitListMatch[];
  /** The next of ours, when one is planned. */
  next: FaceitListMatch | null;
  opponents: FaceitOpponent[];
  /** Our record: [won, lost]. */
  record: [number, number];
}

export interface FaceitMember {
  user: { id: string; name: string; avatarUrl: string | null };
  steamId: string | null;
  /** Null: no FACEIT account for this Steam ID (or none looked up yet). */
  player: FaceitPlayerRef | null;
  checkedAt: string | null;
}

/** A team and FACEIT: its members' profiles and its FACEIT team. */
export interface TeamFaceit {
  /** The server has a FACEIT key: without one, nothing here works. */
  configured: boolean;
  /** Owner and captains link the team. */
  canEdit: boolean;
  team: FaceitTeamRef | null;
  members: FaceitMember[];
  /** FACEIT teams of the members: what the team can be linked to. */
  options: FaceitTeamRef[];
  /** FACEIT's Downloads API is set up: Import works from the list. */
  downloads: boolean;
  /** FACEIT could not be asked just now: what is shown is what was found before. */
  error: string | null;
}

export const LinkFaceitTeamInput = z.object({ teamId: z.string().trim().min(1).max(64).nullable() });
export type LinkFaceitTeamInput = z.infer<typeof LinkFaceitTeamInput>;

export interface FaceitMatches {
  /** The members' FACEIT matches of the last weeks, newest first, each once. */
  recent: FaceitListMatch[];
  /** Championships the team (or its members) played in lately. */
  tournaments: FaceitTournament[];
  /** Members without a FACEIT account found: their matches are missing. */
  missing: string[];
  downloads: boolean;
}

/** Asked while importing: the FACEIT match a demo is from, by its id or by who played when. */
export const FindFaceitMatchInput = z.object({
  /** The library it goes into: its roster says which side is ours. */
  scope: z.string().uuid().nullable().default(null),
  matchId: FaceitMatchId.nullable().default(null),
  /** The demo's players (Steam IDs) and when it was played, when the id is not known. */
  steamIds: z
    .array(z.string().regex(/^\d{17}$/))
    .max(24)
    .default([]),
  playedAt: z.string().datetime({ offset: true }).nullable().default(null),
});
export type FindFaceitMatchInput = z.infer<typeof FindFaceitMatchInput>;

/** What the import fills from a FACEIT match, for one demo of it. */
export interface FaceitMatchFill {
  match: FaceitMatch;
  /** Title: "Our team vs Them", or the two sides' names. */
  title: string;
  event: string;
  stage: string | null;
  bestOf: 1 | 2 | 3 | 5;
  playedAt: string | null;
}

/** The import's fill: the match's details, named from the side the given players are on. */
export function fillFromFaceit(match: FaceitMatch, steamIds: readonly string[] = []): FaceitMatchFill {
  const ids = new Set(steamIds);
  const count = (f: FaceitFaction) => f.players.filter((p) => p.steamId && ids.has(p.steamId)).length;
  // The side most of the given players are on comes first; with no one known, FACEIT's order.
  const first = count(match.factions[1]) > count(match.factions[0]) ? 1 : 0;
  const [a, b] = [match.factions[first], match.factions[1 - first]];
  const bo = match.bestOf;
  return {
    match,
    title: `${a.name} vs ${b.name}`,
    event: match.competition.name,
    stage: match.stage,
    bestOf: bo === 2 || bo === 3 || bo === 5 ? bo : 1,
    playedAt: match.startedAt ?? match.scheduledAt,
  };
}

/** The FACEIT match among candidates that the demo's players played, closest to when it was played. */
export function matchOfPlayers<T extends Pick<FaceitMatch, "factions" | "startedAt" | "finishedAt">>(candidates: readonly T[], steamIds: readonly string[], playedAt: string | null): T | null {
  const ids = new Set(steamIds);
  const at = playedAt ? Date.parse(playedAt) : null;
  let best: { m: T; shared: number; gap: number } | null = null;
  for (const m of candidates) {
    const shared = m.factions.flatMap((f) => f.players).filter((p) => p.steamId && ids.has(p.steamId)).length;
    // Most of the players: a pug's ten, a team's five and five.
    if (shared < Math.min(6, Math.max(1, ids.size))) continue;
    const t = Date.parse(m.finishedAt ?? m.startedAt ?? "");
    const gap = at !== null && Number.isFinite(t) ? Math.abs(t - at) : Number.MAX_SAFE_INTEGER;
    if (!best || shared > best.shared || (shared === best.shared && gap < best.gap)) best = { m, shared, gap };
  }
  return best?.m ?? null;
}
